import { useRef, useState } from 'react';
import EmailAvatar from './EmailAvatar';
import EmailRichTextEditor from './EmailRichTextEditor';
import { emailTypeOptions, parseRecipientPreview } from './emailUtils';

const maxAttachmentFiles = 5;
const maxAttachmentSize = 10 * 1024 * 1024;
const maxAttachmentTotalSize = 20 * 1024 * 1024;
const allowedAttachmentExtensions = new Set(['pdf', 'doc', 'docx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'webp']);
const allowedAttachmentTypes = new Map([
  ['pdf', 'application/pdf'],
  ['doc', 'application/msword'],
  ['docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ['ppt', 'application/vnd.ms-powerpoint'],
  ['pptx', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  ['png', 'image/png'],
  ['jpg', 'image/jpeg'],
  ['jpeg', 'image/jpeg'],
  ['webp', 'image/webp'],
]);

function formatFileSize(size) {
  if (!size) return '0 KB';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(Math.round(size / 1024), 1)} KB`;
}

function getFileExtension(filename = '') {
  const parts = String(filename || '').toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
}

function validateAttachment(file) {
  const extension = getFileExtension(file.name);
  const expectedType = allowedAttachmentTypes.get(extension);

  if (!allowedAttachmentExtensions.has(extension) || expectedType !== file.type) {
    return 'File type not allowed. Use PDF, Office documents, PNG, JPG, JPEG, or WebP.';
  }

  if (file.size > maxAttachmentSize) {
    return 'File too large. Max 10MB per file.';
  }

  return '';
}

function normalizeEmailValue(value) {
  return String(value || '').trim().toLowerCase();
}

function getGroupEmailStats(group, unsubscribedSet) {
  const emails = (group?.emails || []).map((record) => normalizeEmailValue(record.normalizedEmail || record.email)).filter(Boolean);
  const uniqueEmails = Array.from(new Set(emails));
  const unsubscribedCount = uniqueEmails.filter((email) => unsubscribedSet.has(email)).length;

  return {
    duplicateCount: Math.max(emails.length - uniqueEmails.length, 0),
    finalCount: Math.max(uniqueEmails.length - unsubscribedCount, 0),
    unsubscribedCount,
    validCount: uniqueEmails.length,
  };
}

function formatEstimatedSendTime(recipientCount, delayMs) {
  const seconds = Math.ceil((Math.max(recipientCount, 0) * Math.max(Number(delayMs) || 0, 0)) / 1000);

  if (seconds <= 0) return 'under 1 second';
  if (seconds < 30) return `about ${seconds} second${seconds === 1 ? '' : 's'}`;

  const minutes = Math.max(Math.ceil(seconds / 60), 1);
  return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

function EmailCompose({
  bulkDelayMs,
  bulkLimit,
  bulkPreview,
  bulkSendJob,
  form,
  groups = [],
  onPreview,
  onSubmit,
  onUpdateForm,
  senderOptions,
  sendingMode,
  setBulkPreview,
  submitting,
  unsubscribes = [],
}) {
  const fileInputRef = useRef(null);
  const [attachmentError, setAttachmentError] = useState('');
  const recipientPreview = bulkPreview || parseRecipientPreview(form.toText);
  const getSenderKey = (sender) => sender?.key || sender?.senderKey || sender?.mailbox || '';
  const selectedSender = senderOptions.find((sender) => getSenderKey(sender) === form.senderKey) || senderOptions[0];
  const groupedSenders = senderOptions.reduce((groupsByName, sender) => {
    const groupName = sender.group || sender.service || 'Senders';
    return {
      ...groupsByName,
      [groupName]: [...(groupsByName[groupName] || []), sender],
    };
  }, {});
  const selectedGroup = groups.find((group) => group._id === form.groupId);
  const attachments = form.attachments || [];
  const unsubscribedSet = new Set(
    (unsubscribes || []).map((record) => String(record.normalizedEmail || record.email || '').trim().toLowerCase())
  );
  const groupStats = getGroupEmailStats(selectedGroup, unsubscribedSet);
  const manualUnsubscribedCount = recipientPreview.validEmails.filter((email) => unsubscribedSet.has(email)).length;
  const validRecipientCount =
    sendingMode === 'bulk' && form.recipientSource === 'group'
      ? groupStats.validCount
      : recipientPreview.validEmails.length;
  const duplicateCount =
    sendingMode === 'bulk' && form.recipientSource === 'group'
      ? groupStats.duplicateCount
      : recipientPreview.duplicateEmails.length;
  const finalRecipientCount =
    sendingMode === 'bulk' && form.recipientSource === 'group'
      ? groupStats.finalCount
      : Math.max(recipientPreview.validEmails.length - manualUnsubscribedCount, 0);
  const limitExceeded = sendingMode === 'bulk' && finalRecipientCount > bulkLimit;
  const isBulkSending = submitting && sendingMode === 'bulk';
  const estimatedSendTime = formatEstimatedSendTime(finalRecipientCount, bulkDelayMs);

  const runPreview = () => {
    onPreview(parseRecipientPreview(form.toText));
  };

  const updateForm = (event) => {
    onUpdateForm(event);

    if (event.target.name === 'toText') {
      setBulkPreview(null);
    }
  };

  const updateBody = (html) => {
    onUpdateForm({ target: { name: 'body', value: html } });
  };

  const updateAttachments = (nextAttachments) => {
    onUpdateForm({ target: { name: 'attachments', value: nextAttachments } });
  };

  const handleAttachmentChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const nextAttachments = [...attachments];

    setAttachmentError('');

    for (const file of selectedFiles) {
      const error = validateAttachment(file);
      if (error) {
        setAttachmentError(error);
        event.target.value = '';
        return;
      }

      if (nextAttachments.length >= maxAttachmentFiles) {
        setAttachmentError(`Too many files. Attach up to ${maxAttachmentFiles} files.`);
        event.target.value = '';
        return;
      }

      nextAttachments.push(file);
    }

    const totalSize = nextAttachments.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxAttachmentTotalSize) {
      setAttachmentError('Attachment upload failed. Max total size is 20MB per email.');
      event.target.value = '';
      return;
    }

    updateAttachments(nextAttachments);
    event.target.value = '';
  };

  const removeAttachment = (indexToRemove) => {
    setAttachmentError('');
    updateAttachments(attachments.filter((_, index) => index !== indexToRemove));
  };

  return (
    <form className="admin-panel admin-email-compose" onSubmit={onSubmit}>
      <div className="admin-page__header--split">
        <h2>Compose</h2>
        <span>Individual messages only</span>
      </div>
      <p>Marketing and broadcast sending are disabled.</p>

      <label>
        From
        <select name="senderKey" onChange={updateForm} value={form.senderKey || getSenderKey(selectedSender)}>
          {Object.entries(groupedSenders).map(([groupName, senders]) => (
            <optgroup key={groupName} label={groupName}>
              {senders.map((sender) => (
                <option key={getSenderKey(sender)} value={getSenderKey(sender)}>
                  {sender.formatted || sender.from}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      {selectedSender ? (
        <div className="admin-email-sender-preview">
          <EmailAvatar address={selectedSender.email} size="sm" />
          <span>{selectedSender.formatted}</span>
        </div>
      ) : null}
      {sendingMode === 'bulk' ? (
        <label>
          Recipients
          <select name="recipientSource" onChange={updateForm} value={form.recipientSource || 'manual'}>
            <option value="manual">Pasted emails</option>
            <option value="group">Saved group</option>
          </select>
        </label>
      ) : null}
      {sendingMode === 'bulk' && form.recipientSource === 'group' ? (
        <label>
          Saved group
          <select name="groupId" onChange={updateForm} value={form.groupId || ''}>
            <option value="">Choose group</option>
            {groups.map((group) => (
              <option key={group._id} value={group._id}>
                {group.name} ({group.emails?.length || 0})
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label>
        To
        <textarea
          disabled={sendingMode === 'bulk' && form.recipientSource === 'group'}
          name="toText"
          onChange={updateForm}
          placeholder="one email, comma-separated, or one email per line"
          rows="5"
          value={
            sendingMode === 'bulk' && form.recipientSource === 'group' && selectedGroup
              ? selectedGroup.emails?.map((record) => record.email).join('\n')
              : form.toText
          }
        />
      </label>
      <div className="bulk-preview-grid">
        <span>
          Valid: {validRecipientCount}
        </span>
        <span>Invalid: {form.recipientSource === 'group' ? 0 : recipientPreview.invalidEmails.length}</span>
        <span>Duplicates removed: {duplicateCount}</span>
        <span>Final recipients: {finalRecipientCount}</span>
        <span>Limit: {bulkLimit}</span>
      </div>
      {sendingMode === 'bulk' && form.recipientSource === 'group' && selectedGroup ? (
        <p className="admin-email-warning">
          Group selected: {selectedGroup.name}. Total: {selectedGroup.emails?.length || 0}. Unsubscribed skipped:{' '}
          {groupStats.unsubscribedCount}. Final recipients: {groupStats.finalCount}.
        </p>
      ) : null}
      {sendingMode === 'bulk' && form.recipientSource !== 'group' && manualUnsubscribedCount ? (
        <p className="admin-email-warning">
          Unsubscribed skipped: {manualUnsubscribedCount}. Final recipients: {finalRecipientCount}.
        </p>
      ) : null}
      {sendingMode === 'bulk' ? (
        <div className="bulk-preview-panel">
          <p>Estimated send time: {estimatedSendTime}</p>
          {limitExceeded ? (
            <p className="admin-email-warning">
              Bulk send limit is {bulkLimit} recipients per send. Please split into smaller groups.
            </p>
          ) : null}
          {form.recipientSource !== 'group' ? (
            <button className="btn btn--secondary" onClick={runPreview} type="button">
              Preview Recipients
            </button>
          ) : null}
          {form.recipientSource !== 'group' && recipientPreview.invalidEmails.length ? (
            <p className="admin-email-warning">Invalid: {recipientPreview.invalidEmails.join(', ')}</p>
          ) : null}
        </div>
      ) : null}
      {isBulkSending ? (
        <div className="admin-email-send-progress" aria-live="polite">
          <strong>Please do not refresh while sending.</strong>
          <div className="bulk-preview-grid">
            <span>
              Sending {(bulkSendJob?.sent || 0) + (bulkSendJob?.failed || 0)} / {bulkSendJob?.total || 0}
            </span>
            <span>Sent: {bulkSendJob?.sent || 0}</span>
            <span>Failed: {bulkSendJob?.failed || 0}</span>
            <span>Skipped unsubscribed: {bulkSendJob?.skippedUnsubscribed || 0}</span>
            <span>Invalid: {bulkSendJob?.invalid || 0}</span>
          </div>
          {bulkSendJob?.message ? <p>{bulkSendJob.message}</p> : null}
        </div>
      ) : null}
      <label>
        CC
        <input name="ccText" onChange={updateForm} value={form.ccText} />
      </label>
      <label>
        BCC
        <input name="bccText" onChange={updateForm} value={form.bccText} />
      </label>
      <label>
        Email type
        <select name="emailType" onChange={updateForm} value={form.emailType}>
          {emailTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {form.emailType === 'marketing' ? (
        <p className="admin-email-warning">
          Marketing emails should only be sent to permitted contacts. Unsubscribed addresses are skipped and
          unsubscribe links are added automatically.
        </p>
      ) : null}
      <label>
        Subject
        <input name="subject" onChange={updateForm} value={form.subject} />
      </label>
      <label>
        Email body
        <EmailRichTextEditor value={form.body} onChange={updateBody} />
      </label>
      <div className="admin-email-attachment-picker">
        <div>
          <h3>Attachments</h3>
          <p>Attach PDF or document files. Max 10MB per file.</p>
          <p>For large files or many recipients, use a Google Drive link instead of attachment.</p>
        </div>
        <input
          accept=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg,.webp"
          className="sr-only"
          multiple
          onChange={handleAttachmentChange}
          ref={fileInputRef}
          type="file"
        />
        <button className="btn btn--secondary" onClick={() => fileInputRef.current?.click()} type="button">
          Choose files
        </button>
        {attachmentError ? <p className="admin-email-warning">{attachmentError}</p> : null}
        {attachments.length ? (
          <div className="admin-email-selected-attachments">
            {attachments.map((file, index) => (
              <div className="admin-email-selected-attachment" key={`${file.name}-${file.size}-${index}`}>
                <span>{file.name}</span>
                <span>{formatFileSize(file.size)}</span>
                <button onClick={() => removeAttachment(index)} type="button">
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <button className="btn btn--primary" disabled={submitting || limitExceeded} type="submit">
        {submitting ? 'Sending...' : 'Send Email'}
      </button>
    </form>
  );
}

export default EmailCompose;
